/**
 * Magic Cloth CRO
 * Isolated script for sections/magic-cloth-cro.liquid
 *
 * Does not read from, write to, or depend on the Bundle
 * Selector, Add to Cart / product-form.js, or UpCart.
 *
 * Fully self-guarded: every init function below is only
 * called if its own container exists on the page, and the
 * whole file is a no-op if .mc-cro is not present at all.
 */

document.addEventListener("DOMContentLoaded", () => {

    const root = document.querySelector(".mc-cro");

    if (!root) return;

    /**
     * --------------------------------------------------
     * Demo
     * --------------------------------------------------
     */

    function initDemo() {

        const el = root.querySelector(".mc-cro-demo");

        if (!el) return;

        const video = el.querySelector(".mc-cro-media-video");
        const toggle = el.querySelector(".mc-cro-sound-toggle");

        if (!video || !toggle) return;

        toggle.addEventListener("click", () => {
            video.muted = !video.muted;
            toggle.setAttribute("aria-pressed", String(!video.muted));
            toggle.setAttribute("aria-label", video.muted ? "Unmute video" : "Mute video");
            if (!video.muted) video.play();
        });

    }

    /**
     * --------------------------------------------------
     * Shared: horizontal auto-scroll carousel (mobile)
     * --------------------------------------------------
     * Powers Benefits, the Photo Gallery and Written Reviews.
     * Desktop is always a static grid (see each section's CSS
     * for the min-width:750px override), so everything below
     * only matters on mobile.
     *
     * `reverse` flips which direction the carousel auto-scrolls:
     * forward carousels clone the card set and append it after
     * the original, then scroll toward negative X; reverse
     * carousels prepend the clone before the original and scroll
     * toward positive X instead. The two are mirror images of
     * each other, so Written Reviews (reverse: true) slides the
     * opposite way from Benefits and the Photo Gallery.
     */

    function initCarousel({ section, viewportSelector, trackSelector, cardSelector, reverse }) {

        const viewport = section.querySelector(viewportSelector);
        const track = section.querySelector(trackSelector);

        if (!viewport || !track) return;

        const cards = [...track.querySelectorAll(cardSelector)];

        if (cards.length < 2) return;

        const prefersReducedMotion =
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const isMobileLayout = () =>
            !window.matchMedia("(min-width: 750px)").matches;

        const AUTO_SCROLL_SPEED = 45; // px per second — noticeable but unhurried
        const RESUME_DELAY = 1200; // ms after the user releases before auto-scroll resumes
        const DRAG_THRESHOLD = 6; // px of movement before a touch counts as a drag

        /**
         * Fade-in — only enabled when motion is allowed, so the
         * section always renders normally with JS disabled or with
         * prefers-reduced-motion set.
         */

        if (!prefersReducedMotion) {

            track.classList.add("mc-cro-carousel-fade-init");

        }

        /**
         * Seamless loop setup.
         *
         * The card set is cloned once. Forward carousels append the
         * clone after the original set; reverse carousels prepend it
         * before the original set (inserted in original order, right
         * before the first real card) so the wrap math mirrors
         * cleanly. On desktop the clones are hidden entirely via CSS
         * (static grid, nothing to loop or animate).
         */

        let singleSetWidth = 0;

        function measureSetWidth() {

            const firstCard = cards[0];
            const lastCard = cards[cards.length - 1];

            const gap =
                parseFloat(getComputedStyle(track).columnGap) ||
                parseFloat(getComputedStyle(track).gap) ||
                0;

            singleSetWidth =
                (lastCard.offsetLeft + lastCard.offsetWidth) -
                firstCard.offsetLeft +
                gap;

        }

        function cloneCardsForLoop() {

            const referenceNode = reverse ? track.firstElementChild : null;

            cards.forEach(card => {

                const clone = card.cloneNode(true);

                clone.classList.add("mc-cro-carousel-clone");
                clone.setAttribute("aria-hidden", "true");

                if (reverse) {

                    track.insertBefore(clone, referenceNode);

                } else {

                    track.appendChild(clone);

                }

            });

        }

        measureSetWidth();
        cloneCardsForLoop();

        window.addEventListener("resize", measureSetWidth);

        /**
         * Fade-in visibility — runs on every breakpoint, since the
         * fade-init class above is applied unconditionally too. The
         * auto-scroll/drag behaviour below is mobile-only, but this
         * part isn't.
         */

        const visibilityObserver = new IntersectionObserver(entries => {

            entries.forEach(entry => {

                if (entry.isIntersecting) {

                    track.classList.add("is-visible");

                }

            });

        }, { threshold: 0.2 });

        visibilityObserver.observe(section);

        // Desktop is a static grid — no carousel behaviour to wire up.
        if (!isMobileLayout()) return;

        /**
         * Position.
         *
         * A single translateX offset drives both auto-scroll and
         * manual dragging — transform, not scrollLeft. Writing
         * scrollLeft every frame proved unreliable and laggy on iOS
         * specifically (each write can force expensive main-thread
         * work there); transform is compositor-only and doesn't have
         * that cost, on any engine.
         *
         * Reverse carousels start at -singleSetWidth instead of 0 so
         * the first thing shown is still the real card set (the
         * prepended clone sits before that position) — both
         * carousel types render identically on first paint and only
         * differ in which way they animate afterward.
         */

        let currentX = reverse ? -singleSetWidth : 0;

        function applyPosition() {

            if (reverse) {

                if (singleSetWidth > 0 && currentX >= 0) {

                    currentX -= singleSetWidth;

                }

                // Dragging past the very end stops there rather than
                // wrapping — there's no cloned set after the last card.
                if (currentX < -singleSetWidth) {

                    currentX = -singleSetWidth;

                }

            } else {

                if (singleSetWidth > 0 && currentX <= -singleSetWidth) {

                    currentX += singleSetWidth;

                }

                // Dragging back past the very start stops there rather
                // than wrapping — there's no cloned set before card 1.
                if (currentX > 0) {

                    currentX = 0;

                }

            }

            track.style.transform = "translateX(" + currentX + "px)";

        }

        /**
         * Auto-scroll
         */

        let isPaused = prefersReducedMotion;
        let rafId = null;
        let lastTimestamp = null;
        let resumeTimer = null;

        const direction = reverse ? 1 : -1;

        function tick(timestamp) {

            rafId = requestAnimationFrame(tick);

            if (lastTimestamp === null) {

                lastTimestamp = timestamp;
                return;

            }

            const delta = timestamp - lastTimestamp;
            lastTimestamp = timestamp;

            if (isPaused || singleSetWidth <= 0) return;

            currentX += direction * (AUTO_SCROLL_SPEED * delta) / 1000;
            applyPosition();

        }

        function startAutoScroll() {

            if (rafId !== null || prefersReducedMotion) return;

            lastTimestamp = null;
            rafId = requestAnimationFrame(tick);

        }

        function stopAutoScroll() {

            if (rafId === null) return;

            cancelAnimationFrame(rafId);
            rafId = null;

        }

        function pause() {

            isPaused = true;

            if (resumeTimer) {

                clearTimeout(resumeTimer);
                resumeTimer = null;

            }

        }

        function scheduleResume() {

            if (prefersReducedMotion) return;

            if (resumeTimer) clearTimeout(resumeTimer);

            resumeTimer = setTimeout(() => {

                isPaused = false;
                resumeTimer = null;

            }, RESUME_DELAY);

        }

        /**
         * Manual drag.
         *
         * Native Touch Events are used (not Pointer Events) because
         * reliably suppressing the page's own vertical scroll for a
         * confirmed-horizontal gesture requires calling
         * preventDefault() on touchmove specifically, registered as
         * non-passive.
         *
         * Direction is only decided after a small movement threshold.
         * Until then nothing is prevented. If the gesture turns out
         * to be vertical, it's left alone for the rest of the
         * gesture — the page scrolls exactly as it would if this
         * carousel didn't exist at all.
         */

        let touchStartX = 0;
        let touchStartY = 0;
        let dragStartX = 0;
        let directionDecided = false;
        let isDragging = false;

        track.addEventListener("touchstart", e => {

            const touch = e.touches[0];

            touchStartX = touch.clientX;
            touchStartY = touch.clientY;
            dragStartX = currentX;
            directionDecided = false;
            isDragging = false;

        }, { passive: true });

        track.addEventListener("touchmove", e => {

            const touch = e.touches[0];
            const deltaX = touch.clientX - touchStartX;
            const deltaY = touch.clientY - touchStartY;

            if (!directionDecided) {

                if (
                    Math.abs(deltaX) < DRAG_THRESHOLD &&
                    Math.abs(deltaY) < DRAG_THRESHOLD
                ) {

                    return;

                }

                directionDecided = true;
                isDragging = Math.abs(deltaX) > Math.abs(deltaY);

                if (isDragging) pause();

            }

            if (!isDragging) return; // vertical gesture — untouched

            e.preventDefault();

            currentX = dragStartX + deltaX;
            applyPosition();

        }, { passive: false });

        function endTouch() {

            if (isDragging) scheduleResume();

            directionDecided = false;
            isDragging = false;

        }

        track.addEventListener("touchend", endTouch);
        track.addEventListener("touchcancel", endTouch);

        /**
         * Only auto-scroll while the section is actually in view.
         *
         * Starting is delayed until just after the fade-in transition
         * finishes, rather than firing both at the exact same instant.
         */

        let startTimer = null;

        const autoScrollObserver = new IntersectionObserver(entries => {

            entries.forEach(entry => {

                if (entry.isIntersecting) {

                    if (startTimer) clearTimeout(startTimer);

                    startTimer = setTimeout(() => {

                        startTimer = null;
                        startAutoScroll();

                    }, 650);

                } else {

                    if (startTimer) {

                        clearTimeout(startTimer);
                        startTimer = null;

                    }

                    stopAutoScroll();

                }

            });

        }, { threshold: 0.2 });

        autoScrollObserver.observe(section);

        applyPosition();

    }

    /**
     * --------------------------------------------------
     * Benefits ("Why You'll Love It")
     * --------------------------------------------------
     */

    function initBenefits() {

        const section = root.querySelector(".mc-cro-benefits");

        if (!section) return;

        initCarousel({
            section,
            viewportSelector: ".mc-cro-benefits-viewport",
            trackSelector: ".mc-cro-benefits-grid",
            cardSelector: ".mc-cro-benefit-card",
            reverse: false
        });

    }

    /**
     * --------------------------------------------------
     * Uses ("Uses Around Your Home")
     * --------------------------------------------------
     */

    function initUses() {

        const el = root.querySelector(".mc-cro-uses");

        if (!el) return;

    }

    /**
     * --------------------------------------------------
     * Comparison ("Comparison Table")
     * --------------------------------------------------
     */

    function initComparison() {

        const el = root.querySelector(".mc-cro-comparison");

        if (!el) return;

    }

    /**
     * --------------------------------------------------
     * Reviews ("Customer Reviews")
     * --------------------------------------------------
     */

    function initReviewGallery() {

        const section = root.querySelector(".mc-cro-reviews");

        if (!section) return;

        initCarousel({
            section,
            viewportSelector: ".mc-cro-review-gallery-viewport",
            trackSelector: ".mc-cro-review-gallery",
            cardSelector: ".mc-cro-review-gallery-card",
            reverse: false
        });

    }

    function initReviewGrid() {

        const section = root.querySelector(".mc-cro-reviews");

        if (!section) return;

        initCarousel({
            section,
            viewportSelector: ".mc-cro-review-grid-viewport",
            trackSelector: ".mc-cro-review-grid",
            cardSelector: ".mc-cro-review-card",
            reverse: true
        });

    }

    /**
     * --------------------------------------------------
     * Shipping ("Shipping Information")
     * --------------------------------------------------
     */

    function initShipping() {

        const el = root.querySelector(".mc-cro-shipping");

        if (!el) return;

    }

    /**
     * --------------------------------------------------
     * Guarantee ("Shop With Confidence")
     * --------------------------------------------------
     */

    function initGuarantee() {

        const el = root.querySelector(".mc-cro-guarantee");

        if (!el) return;

    }

    /**
     * --------------------------------------------------
     * FAQ ("Frequently Asked Questions")
     *
     * One accordion item open at a time. Animation itself is
     * handled purely in CSS via the .is-open class (a
     * grid-template-rows transition) — this just toggles that
     * class plus the accessibility attributes.
     * --------------------------------------------------
     */

    function initFAQ() {

        const el = root.querySelector(".mc-cro-faq");

        if (!el) return;

        const items = [...el.querySelectorAll(".mc-cro-faq-item")];

        if (!items.length) return;

        function closeItem(item) {

            const trigger = item.querySelector(".mc-cro-faq-trigger");
            const answer = item.querySelector(".mc-cro-faq-answer");

            item.classList.remove("is-open");

            if (trigger) trigger.setAttribute("aria-expanded", "false");
            if (answer) answer.setAttribute("aria-hidden", "true");

        }

        function openItem(item) {

            const trigger = item.querySelector(".mc-cro-faq-trigger");
            const answer = item.querySelector(".mc-cro-faq-answer");

            item.classList.add("is-open");

            if (trigger) trigger.setAttribute("aria-expanded", "true");
            if (answer) answer.setAttribute("aria-hidden", "false");

        }

        items.forEach(item => {

            closeItem(item);

            const trigger = item.querySelector(".mc-cro-faq-trigger");

            if (!trigger) return;

            trigger.addEventListener("click", () => {

                const wasOpen = item.classList.contains("is-open");

                items.forEach(closeItem);

                if (!wasOpen) openItem(item);

            });

        });

    }

    initDemo();
    initBenefits();
    initUses();
    initComparison();
    initReviewGallery();
    initReviewGrid();
    initShipping();
    initGuarantee();
    initFAQ();

});
